import { useCallback, useEffect, useRef, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import cx from 'classnames';
import { stringifyUrl } from 'query-string';

import { TContact, TUserWithConnect } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import { useContactContext } from 'lib/common/contexts/ContactContext';
import { useModulesContext } from 'lib/common/contexts/ModulesContext';
import { useOverlayContext } from 'lib/common/contexts/OverlayContext';
import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import Loader from 'lib/common/components/Loader';
import { LogEvents, logger } from 'lib/common/components/LoggerController';

import requestErrorToast from 'lib/common/utils/toast/requestErrorToast';

import { DIRECTORY_TAB } from '../../../constants/directoryTabs';
import { TContactListData } from '../../../hooks/usePaginatedLoad';
import { TInternalContacts } from '../../../types';
import Contact from '../../Contact/Contact';
import DirectoryPlaceHolder from './DirectoryPlaceHolder';
import { formatName, transformUser } from './utils';

import styles from './contact-list.module.scss';

type TPaginatedAgentResponse = { data: { users: TUserWithConnect[]; count: number; totalPages: number } };
type TVirtualizedProps = {
  rootDivProps: {
    ref: (node: Element | null | undefined) => void;
    'data-index': number;
    style: { transform: string; position: 'absolute'; width: string };
  };
};

// need to be mindful of the numbers used here bad configurations can cause render issues
const DATABASE_PAGE_SIZE = 30;
const OVERSCAN_THRESHOLD = 5;
const REFETCH_MS = 5000;

export const getItemsPageNumber = (contactArrayIndex: number) => {
  return Math.ceil((contactArrayIndex + 1) / DATABASE_PAGE_SIZE);
};

const [desktopRowHeight, softphoneRowHeight, gapSpace] = [40, 30, 20];

// initial virtualizer approach using below,
// would highly recommend looking at this as a base but starting from scratch if creating a generic component
// https://tanstack.com/virtual/latest/docs/api/virtualizer
// https://github.com/iammarmirza/tanstack-virtualized-list/blob/main/app/page.tsx
const AgentContactList = ({
  searchTerm,
  selectedTab,
  isAdmin,
  isTransfer,
  onCreateOrUpdateClick,
  onClearSearchTerm,
  transferringToId,
  transferDisabled,
  onDeleteContact,
  onTransfer,
  isDrawer,
}: {
  searchTerm: string;
  selectedTab: DIRECTORY_TAB;
  isAdmin: boolean;
  isTransfer: boolean;
  onCreateOrUpdateClick?: (contact?: TInternalContacts) => void;
  onClearSearchTerm: () => void;
  transferringToId?: string;
  onDeleteContact?: () => void;
  transferDisabled?: boolean;
  onTransfer: (contact: TContactListData) => Promise<void>;
  isDrawer?: boolean;
}) => {
  const [loading, setLoading] = useState(true);

  const {
    actions: { makeOutboundCall },
  } = useContactContext();
  const { isSoftphone } = useLayout();

  const { closeOverlay } = useOverlayContext();

  const { fetch_ } = useAuthContext();
  const { config } = useConfigContext();

  const {
    state: { widgetModules = [] },
  } = useModulesContext();

  const errorMessage = `Something went wrong and we couldn't fetch agent information. Please try again.`;

  const fetchData = async ({ pageParam }: { pageParam?: number }) => {
    const page = pageParam ?? 1;
    const queryUrl = stringifyUrl({
      url: `${config.AGENT_SERVICE_V2_URL}/agent/users`,
      query: {
        search: searchTerm,
        withConnectInformation: true,
        page,
        pageSize: DATABASE_PAGE_SIZE,
      },
    });

    try {
      const res = await (await fetch_(queryUrl)).json();
      const {
        data: { users, totalPages },
      }: TPaginatedAgentResponse = res;

      const transformedUsers = users?.map(transformUser) || [];

      setLoading(false);
      return { data: transformedUsers, totalPages, page };
    } catch (e: any) {
      requestErrorToast({ errorMessage, errorReference: e.requestId });

      logger.error(LogEvents.CONTACT.LOAD.FAIL, { error: e, searchTerm, selectedTab });
      return { data: [], totalPages: 0, page: 1 };
    }
  };

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    queryKey: ['agents', searchTerm],
    queryFn: fetchData,
    getNextPageParam: (lastPage) => {
      if (!lastPage.page) {
        return undefined;
      }

      return lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const parentRef = useRef(null);
  const contacts = data?.pages.flatMap((page) => page.data.flat()) || [];

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? contacts.length + 1 : contacts.length,
    getScrollElement: useCallback(() => parentRef.current, []),
    estimateSize: useCallback(() => (isSoftphone ? softphoneRowHeight : desktopRowHeight), [isSoftphone]),
    gap: gapSpace,
    overscan: OVERSCAN_THRESHOLD,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const loaderIndex = contacts.length;

  const isDrawerNotSoftPhone = isDrawer && !isSoftphone;

  // determines if we need to load the next page of data due to it scrolling into the page, or the page has grown
  useEffect(() => {
    const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? 0;

    const shouldFetch = lastVisibleIndex >= loaderIndex && hasNextPage && !isFetchingNextPage;

    const scrollOffset = rowVirtualizer.scrollOffset ?? 0;

    if (scrollOffset === 0) {
      return;
    }

    if (shouldFetch) {
      fetchNextPage();
    }
  }, [virtualItems, loaderIndex, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Reset loading and mount flag when search term changes (query automatically resets due to queryKey)
  useEffect(() => {
    setLoading(true);
    rowVirtualizer.scrollToIndex(0);
  }, [searchTerm]);

  // Viewport-aware polling: only refetch pages whose rows are currently visible
  useEffect(() => {
    const refetchInterval = setInterval(() => {
      if (rowVirtualizer.isScrolling) {
        return;
      }

      const renderedRows = rowVirtualizer.getVirtualItems();

      if (!renderedRows.length) {
        return;
      }

      // Use the middle visible item to determine the current page, avoiding
      // overscan rows from adjacent pages messing the page calculation.
      const middleRow = renderedRows[Math.floor(renderedRows.length / 2)];
      const currentPage = getItemsPageNumber(middleRow.index);

      refetch({ refetchPage: (_, index) => index + 1 === currentPage });
    }, REFETCH_MS);

    return () => clearInterval(refetchInterval);
  }, [refetch]);

  const onCallClick = async (row: TContact) => {
    if (!('phoneNumber' in row)) {
      return;
    }

    await makeOutboundCall(row.phoneNumber);
    closeOverlay();
  };

  const contactElements = rowVirtualizer.getVirtualItems().map((virtualRow) => {
    const isLoaderRow = virtualRow.index > contacts.length - 1;
    const contact = contacts[virtualRow.index];

    const fullName = formatName(contact?.firstName, contact?.lastName);

    const virtualListProps: TVirtualizedProps = {
      rootDivProps: {
        'data-index': virtualRow.index,
        ref: rowVirtualizer.measureElement,
        style: {
          position: 'absolute',
          transform: `translateY(${virtualRow.start}px)`,
          width: '100%',
        },
      },
    };

    if (!contact) {
      return isLoaderRow && hasNextPage ? (
        <div {...virtualListProps.rootDivProps} key={'agent-contact-list-loader'}>
          <Loader key={'virtual-list-loader'} className={styles['agent-contact-list__container__internal__loader']} />
        </div>
      ) : null;
    }

    return (
      <div className="full-width" key={`virtual-contact-${virtualRow.index}`} {...virtualListProps.rootDivProps}>
        <Contact
          contact={contact}
          onCallClick={onCallClick}
          transferDisabled={transferDisabled}
          isTransfer={isTransfer}
          searchTerm={searchTerm}
          isSoftphone={isSoftphone}
          fullName={fullName}
          onTransfer={onTransfer}
          isAdmin={isAdmin}
          onCreateOrUpdateClick={onCreateOrUpdateClick}
          onDeleteContact={onDeleteContact}
          transferringToId={transferringToId}
        />
      </div>
    );
  });

  return (
    <>
      <DirectoryPlaceHolder
        isAdmin={isAdmin}
        selectedTab={selectedTab}
        searchTerm={searchTerm}
        onClearSearchTerm={onClearSearchTerm}
        loading={loading}
        numberOfContacts={contacts.length}
        onCreateOrUpdateClick={onCreateOrUpdateClick}
      />
      {/* The scrollable element for your list */}
      {Boolean(contacts.length) && (
        <div
          role="tabpanel"
          ref={parentRef}
          className={cx(styles['agent-contact-list__container'], {
            [styles['agent-contact-list__container--softphone']]: isSoftphone,
            [styles['agent-contact-list__container--drawer']]: isDrawer,
            [styles['agent-contact-list__container--drawer__no-widget']]: isDrawer && !widgetModules.length,
            [styles['agent-contact-list__container--drawer__not-softphone']]: isDrawerNotSoftPhone,
          })}
        >
          {/* virtual view*/}
          <div
            className={cx(styles['agent-contact-list__container__internal'], {
              [styles['agent-contact-list__container--softphone__internal']]: isSoftphone,
            })}
            style={{
              height: rowVirtualizer.getTotalSize(),
            }}
          >
            {contactElements}
          </div>
        </div>
      )}
    </>
  );
};

export default AgentContactList;
